import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface BreadcrumbItem {
  text: string;
  to?: string;
}

export interface TopBarAction {
  id: string;
  label: string;
  onClick: () => void | Promise<void>;
  appearance?: 'primary' | 'secondary' | 'outline' | 'subtle' | 'transparent';
  disabled?: boolean;
  icon: React.ReactElement;
  triggerRef?: React.RefObject<HTMLButtonElement | null>;
}

interface TopBarContextType {
  title: string;
  /** Custom ReactNode rendered in place of the plain text title */
  titleNode: ReactNode | null;
  actions: ReactNode | null;
  actionItems: readonly TopBarAction[] | null;
  breadcrumbs: BreadcrumbItem[];
  setTitle: (title: string) => void;
  setTitleNode: (node: ReactNode | null) => void;
  setActions: (actions: ReactNode | null) => void;
  setActionItems: (actions: readonly TopBarAction[] | null) => void;
  setBreadcrumbs: (breadcrumbs: BreadcrumbItem[]) => void;
}

const TopBarContext = createContext<TopBarContextType>({
  title: '',
  titleNode: null,
  actions: null,
  actionItems: null,
  breadcrumbs: [],
  setTitle: () => {},
  setTitleNode: () => {},
  setActions: () => {},
  setActionItems: () => {},
  setBreadcrumbs: () => {},
});

export const TopBarProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [title, setTitle] = useState('');
  const [titleNode, setTitleNode] = useState<ReactNode | null>(null);
  const [actions, setActions] = useState<ReactNode | null>(null);
  const [actionItems, setActionItems] = useState<readonly TopBarAction[] | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);

  return (
    <TopBarContext.Provider value={{ title, titleNode, actions, actionItems, breadcrumbs, setTitle, setTitleNode, setActions, setActionItems, setBreadcrumbs }}>
      {children}
    </TopBarContext.Provider>
  );
};

/**
 * Hook for pages to set the TopBar title and actions.
 * Automatically cleans up on unmount.
 */
export const useTopBar = () => {
  const ctx = useContext(TopBarContext);
  return ctx;
};

/**
 * Hook that sets the TopBar title and cleans up on unmount.
 */
export const useTopBarTitle = (title: string) => {
  const { setTitle } = useContext(TopBarContext);
  useEffect(() => {
    if (title) {
      setTitle(title);
      return () => setTitle('');
    }
  }, [title, setTitle]);
};

/**
 * Hook that sets a custom ReactNode as the TopBar title and cleans up on unmount.
 */
export const useTopBarTitleNode = (node: ReactNode) => {
  const { setTitleNode } = useContext(TopBarContext);
  useEffect(() => {
    setTitleNode(node);
    return () => setTitleNode(null);
  }, [node, setTitleNode]);
};

/**
 * Hook that sets the TopBar actions and cleans up on unmount.
 */
export const useTopBarActions = (actions: ReactNode) => {
  const { setActions } = useContext(TopBarContext);
  useEffect(() => {
    setActions(actions);
    return () => setActions(null);
  }, [actions, setActions]);
};

/** Registers typed actions that the TopBar can safely move into its overflow menu. */
export const useTopBarActionItems = (actions: readonly TopBarAction[] | null) => {
  const { setActionItems } = useContext(TopBarContext);
  useEffect(() => {
    setActionItems(actions);
    return () => setActionItems(null);
  }, [actions, setActionItems]);
};

/**
 * Hook that sets the TopBar breadcrumbs and cleans up on unmount.
 */
export const useTopBarBreadcrumbs = (breadcrumbs: BreadcrumbItem[]) => {
  const { setBreadcrumbs } = useContext(TopBarContext);
  useEffect(() => {
    setBreadcrumbs(breadcrumbs);
    return () => setBreadcrumbs([]);
  }, [JSON.stringify(breadcrumbs), setBreadcrumbs]);
};

export default TopBarContext;
